/**
 * toolBindings.js — Enterprise Tool Binding Engine
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * EXPORTS (16):
 *   initToolBindings, destroyToolBindings,
 *   undoAnnotation, redoAnnotation,
 *   initHotkeyActions,
 *   invertViewport, flipViewport, rotateViewport,
 *   takeScreenshot, exportAnnotationsFile, importAnnotationsFile,
 *   getUndoRedoState, clearUndoHistory,
 *   getToolHistory, getToolFavorites, toggleToolFavorite,
 *
 * Re-exports via pass-through:
 *   bindAnnotationEvents, bindUndoRedoKeys, _updateUndoRedoButtons
 *
 * [ENTERPRISE UPGRADE]
 *  - Tool history (last 30 used tools)
 *  - Tool favorites (persisted to localStorage)
 *  - Undo/redo state inspector
 *  - Clear undo history
 *  - All viewport transforms exported (invert, flip, rotate)
 *  - Screenshot API with quality/format options
 *  - Annotation batch operations
 *  - AbortController cleanup
 */

import { setActiveTool, getActiveTool, clearAnnotations, exportAnnotations, importAnnotations, getAnnotations, DISPLAY_TOGGLE_TOOLS, isDisplayToolEnabled } from '../tools/toolManager.js';
import { enableHUProbe, disableHUProbe } from '../core/viewportAnalysis.js'; // [10.19.43] проба переехала в тулбар
import { resetViewport, getRenderingEngine, getViewportIds, toggleMprAlignMode, getMprAlignMode, setStandardView } from '../core/viewport.js'; // [11.1.26] сброс 3D-ракурса
import { getCinematicRenderer } from '../core/viewportState.js';
import { downloadBlob, canvasToBlob, showToast, formatPatientName } from '../utils/helpers.js';
import { screenshotViewport } from '../core/viewport.js';
import { initHotkeys, registerAction } from './hotkeys.js';
import { emit, on as busOn, EVENTS } from '../core/eventBus.js';
import { registerAPIs } from '../core/viewerAPI.js';
import { changeLayout } from './layoutManager.js';
import { activateViewMode } from './layoutManager.js';
import { log } from '../utils/logger.js';
import { showToolHelp } from './inlineHelp.js';

// ══════════════════════════════════════════════════════════════
// STATE
// ══════════════════════════════════════════════════════════════

const _annotationUndoStack = [];
const _annotationRedoStack = [];
const UNDO_MAX = 50;
const _deepClone = (obj) => {
  try { return structuredClone(obj); }
  catch { return JSON.parse(JSON.stringify(obj)); }
};
const $ = (id) => document.getElementById(id);
const $$ = (sel) => document.querySelectorAll(sel);
const on = (id, evt, fn) => { const el = $(id); if (el) el.addEventListener(evt, fn, { signal: _getSignal() }); };
const onAll = (sel, evt, fn) => $$(sel).forEach(el => el.addEventListener(evt, fn, { signal: _getSignal() }));
let _abortController = null;
function _getSignal() { if (!_abortController) _abortController = new AbortController(); return _abortController.signal; }

let _ctx = null;

// ── Enterprise: Tool History & Favorites ──
const TOOL_HISTORY_MAX = 30;
const FAV_LS_KEY = 'mikhmel_tool_favorites';
/** @type {Array<{tool:string, time:number}>} */
const _toolHistory = [];
/** @type {Set<string>} */
let _toolFavorites = new Set();

// Load favorites from localStorage
try {
  const raw = localStorage.getItem(FAV_LS_KEY);
  if (raw) _toolFavorites = new Set(JSON.parse(raw));
} catch { /* ignore */ }

// ══════════════════════════════════════════════════════════════
// 1. INIT / DESTROY
// ══════════════════════════════════════════════════════════════

/**
 * @param {Object} ctx
 * @param {function():string} ctx.getCurrentViewportId
 * @param {function():string} ctx.getCurrentSeries
 * @param {function():string[]} ctx.getCurrentImageIds
 * @param {function():string} ctx.getCurrentVolumeId
 * @param {function():number} ctx.getSliceStep
 * @param {function(number)} ctx.setSliceStep
 * @param {function(string):Promise} ctx.loadSeriesIntoViewport
 * @param {function(string)} ctx.openModal
 */
/**
 * [10.18.0] Колесо над панелью инструментов листает её вбок.
 *
 * Панель — одна строка с горизонтальной прокруткой (см. пояснение у #toolbar
 * в разметке: раньше не влезшие инструменты переносились на вторую строку и
 * та обрезалась — восемь кнопок пропадали совсем). Прокрутка их вернула, но
 * добраться до них можно было только тонкой полоской внизу или Shift+колесо,
 * а этого никто не угадает. Обычное колесо над панелью — угадывается само.
 *
 * Вертикальную прокрутку страницы при этом не трогаем: панель узкая, и увести
 * из-под курсора страницу было бы хуже, чем не долистать инструменты.
 */
function bindToolbarWheel() {
  const бар = document.getElementById('toolbar');
  if (!бар) return;
  бар.addEventListener('wheel', (e) => {
    const есть = бар.scrollWidth - бар.clientWidth;
    if (есть <= 1) return;                       // всё влезло — колесо ни при чём
    const шаг = e.deltaY || e.deltaX;
    if (!шаг) return;
    // Уже упёрлись в край в ту же сторону — отдаём событие странице.
    const вправо = шаг > 0;
    if ((вправо && бар.scrollLeft >= есть - 1) || (!вправо && бар.scrollLeft <= 0)) return;
    e.preventDefault();
    бар.scrollLeft += шаг;
  }, { passive: false });
}

/**
 * [10.18.0] Сколько места занимает правая группа верхнего ряда — измеряем, а
 * не назначаем числом.
 *
 * На ноутбуке 1366 правая группа плавающая (position: fixed), и левой части
 * ряда отводилось фиксированное место — 300 точек. Но правая группа растёт:
 * в неё приходят значки качества, режима, контекста. Когда она перерастает
 * отведённое, пункты меню уезжают ПОД неё и становятся недоступны. Измерением
 * найдено восемь таких кнопок.
 *
 * Любое новое число здесь устареет при первом же новом значке. Поэтому ширина
 * меряется у настоящего элемента и отдаётся вёрстке переменной.
 */
function bindTopbarReserve() {
  const правая = document.querySelector('.topbar-right');
  const ряд = document.querySelector('.topbar');
  if (!правая || !ряд) return;
  const пересчитать = () => {
    try {
      const плавает = getComputedStyle(правая).position === 'fixed';
      const w = плавает ? Math.ceil(правая.getBoundingClientRect().width) : 0;
      ряд.style.paddingRight = w ? `${w + 10}px` : '';
    } catch (e) { /* вёрстка не должна ронять программу */ }
  };
  пересчитать();
  window.addEventListener('resize', пересчитать);
  try { new ResizeObserver(пересчитать).observe(правая); } catch (e) { /* нет наблюдателя — хватит resize */ }
  // Значки приходят асинхронно (качество, режим отрисовки): без наблюдения за
  // содержимым ширина осталась бы измеренной до их появления.
  try { new MutationObserver(пересчитать).observe(правая, { childList: true, subtree: true }); }
  catch (e) { /* */ }
}

// [10.19.43] HU-проба в основном инструментарии (решение владельца, п.4).
// Переключатель; выбор любого инструмента [data-tool] гасит пробу — она не
// смеет висеть призраком поверх чужого курсора.
let _huProbeOn = false;
function _пробаВыкл(кнопка) {
  if (!_huProbeOn) return;
  _huProbeOn = false;
  try { disableHUProbe(); } catch (e) { /* уже выключена */ }
  кнопка?.classList.remove('active');
}
export function bindHUProbe() {
  const кнопка = document.getElementById('btn-tool-hu-probe');
  if (!кнопка) return;
  кнопка.addEventListener('click', () => {
    _huProbeOn = !_huProbeOn;
    if (_huProbeOn) { enableHUProbe(); кнопка.classList.add('active'); }
    else { disableHUProbe(); кнопка.classList.remove('active'); }
  });
  кнопка.closest('.toolbar, #toolbar, [class*="tool"]')?.addEventListener('click', (e) => {
    if (e.target.closest('[data-tool]')) _пробаВыкл(кнопка);
  });
}

export function initToolBindings(ctx) {
  bindHUProbe(); // [10.19.43]
  _ctx = ctx;
  _abortController = new AbortController();
  bindToolbar();
  bindToolbarWheel();
  bindTopbarReserve();
  bindSliceStep();
  bindTopbarActions();
  bindAnnotationEvents();
  bindUndoRedoKeys();
  bindCineSpaceKey(); // [FIX #13] Direct Space key handler for cine
  log.debug('[ToolBindings] Initialized');
}

export function destroyToolBindings() {
  if (_abortController) { _abortController.abort(); _abortController = null; }
  _toolHistory.length = 0;
  log.debug('[ToolBindings] Destroyed');
}

// ══════════════════════════════════════════════════════════════
// [FIX #13] DIRECT SPACE KEY HANDLER FOR CINE
// Bypasses the entire hotkeys chain — guaranteed to work.
// ══════════════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════════════
// DIRECT CINE VIA SPACE — bypasses viewportCine.js entirely
// ══════════════════════════════════════════════════════════════

let _directCineTimer = null;
let _directCineVpId = null;

// [П.4] Общий тумблер автопрокрутки: Пробел и кнопка ▶ в тулбаре — один механизм
function toggleDirectCine() {
    // Toggle: if running — stop
    if (_directCineTimer) {
      clearInterval(_directCineTimer);
      _directCineTimer = null;
      _directCineVpId = null;
      for (const _id of ['cine-play', 'btn-cine-toolbar']) {
        const _b = $(_id);
        if (_b) { _b.textContent = '▶'; _b.classList.remove('active'); }
      }
      console.log('[Cine/Space] Stopped');
      return;
    }

    // Start: find viewport
    const re = getRenderingEngine();
    if (!re) return;

    let vpId = _ctx?.getCurrentViewportId?.();
    let vp = vpId ? re.getViewport(vpId) : null;
    
    // Fallback to first stack or orthographic viewport
    if (!vp) {
      for (const candidate of re.getViewports()) {
        if (candidate.type === 'stack' || candidate.type === 'orthographic') {
          vp = candidate; vpId = candidate.id; break;
        }
      }
    }
    if (!vp) { console.warn('[Cine/Space] No viewport'); return; }

    const fpsEl = $('cine-fps');
    const fps = fpsEl ? parseInt(fpsEl.value) || 15 : 15;
    _directCineVpId = vpId;

    console.log(`[Cine/Space] Direct scroll: ${vpId} type=${vp.type} fps=${fps}`);

    _directCineTimer = setInterval(() => {
      try {
        const currentVp = re.getViewport(_directCineVpId);
        if (!currentVp) { clearInterval(_directCineTimer); _directCineTimer = null; return; }
        
        if (currentVp.type === 'stack') {
          const ids = currentVp.getImageIds();
          const cur = currentVp.getCurrentImageIdIndex() || 0;
          const next = cur + 1 >= ids.length ? 0 : cur + 1;
          currentVp.setImageIdIndex(next);
        } else {
          // Orthographic: use scroll(1)
          currentVp.scroll(1);
        }
        currentVp.render();
      } catch (err) {
        // Don't stop — just skip this frame
      }
    }, Math.round(1000 / fps));

    for (const _id of ['cine-play', 'btn-cine-toolbar']) {
      const _b = $(_id);
      if (_b) { _b.textContent = '⏸'; _b.classList.add('active'); }
    }
    console.log(`[Cine/Space] Running at ${fps}fps on ${vpId}`);

}

function bindCineSpaceKey() {
  document.addEventListener('keydown', (e) => {
    if (e.code !== 'Space') return;
    if (e.ctrlKey || e.altKey || e.metaKey || e.shiftKey) return;
    const tag = e.target?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target?.isContentEditable) return;
    
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    toggleDirectCine();
  }, { capture: true, signal: _getSignal() });
  const _tbBtn = $('btn-cine-toolbar');
  if (_tbBtn) _tbBtn.addEventListener('click', toggleDirectCine, { signal: _getSignal() });
  console.log('[Cine/Space] Listener attached');
}

// ══════════════════════════════════════════════════════════════
// 2. TOOLBAR — all tool buttons
// ══════════════════════════════════════════════════════════════

function bindToolbar() {
  onAll('.tool-btn[data-tool]', 'click', (e) => {
    const toolName = e.currentTarget.dataset.tool;

    // [FIX #11] Display overlay tools → toggle on/off, don't change primary tool
    if (DISPLAY_TOGGLE_TOOLS.has(toolName)) {
      const result = setActiveTool(toolName); // internally calls toggleDisplayTool
      // Toggle the button's active class to reflect on/off state
      e.currentTarget.classList.toggle('active', isDisplayToolEnabled(toolName));
      const state = isDisplayToolEnabled(toolName) ? 'ON' : 'OFF';
      showToast(`${toolName}: ${state}`, 'info', 1200);
      showToolHelp(toolName, e.currentTarget);
      _pushToolHistory(toolName);
      return;
    }

    // Magnify → auto-switch to 2D
    if (toolName === 'Magnify') {
      const re = getRenderingEngine();
      const vp = re?.getViewport(_ctx.getCurrentViewportId());
      if (vp && vp.type !== 'stack') {
        showToast('🔎 Переключение в 2D для лупы...', 'info', 1500);
        activateViewMode('2d');
        setTimeout(() => {
          setActiveTool('Magnify');
          $$('.tool-btn').forEach(b => {
            if (!DISPLAY_TOGGLE_TOOLS.has(b.dataset.tool)) b.classList.toggle('active', b.dataset.tool === 'Magnify');
          });
          const label = $('active-tool-label');
          if (label) label.textContent = 'Magnify';
          const toolOverlay = $('overlay-tool');
          if (toolOverlay) toolOverlay.textContent = 'Magnify';
        }, 800);
        return;
      }
    }

    setActiveTool(toolName);
    showToolHelp(toolName, e.currentTarget);
    // [FIX #11] Only toggle non-display-tool buttons; leave display toggles as-is
    $$('.tool-btn').forEach(b => {
      if (!DISPLAY_TOGGLE_TOOLS.has(b.dataset.tool)) b.classList.remove('active');
    });
    e.currentTarget.classList.add('active');
    const label = $('active-tool-label');
    if (label) label.textContent = toolName;
    const toolOverlay = $('overlay-tool');
    if (toolOverlay) toolOverlay.textContent = toolName;
    if (window.showToolIndicator && window.innerWidth <= 768) {
      window.showToolIndicator(toolName);
    }
    if (window._pushRecentTool) window._pushRecentTool(toolName);

    // Enterprise: track history
    _pushToolHistory(toolName);
  });
}

// ══════════════════════════════════════════════════════════════
// 3. SLICE STEP
// ══════════════════════════════════════════════════════════════

function bindSliceStep() {
  const sel = $('slice-step');
  if (!sel) return;
  sel.value = String(_ctx.getSliceStep());
  sel.addEventListener('change', () => {
    _ctx.setSliceStep(parseInt(sel.value) || 1);
    log.debug(`[UI] Slice step = ${_ctx.getSliceStep()}`);
    showToast(`Шаг среза: ${_ctx.getSliceStep() === 1 ? 'все срезы' : 'каждый ' + _ctx.getSliceStep() + '-й'}`, 'info', 1500);
    if (_ctx.getCurrentSeries()) {
      _ctx.loadSeriesIntoViewport(_ctx.getCurrentSeries());
    }
  }, { signal: _getSignal() });
}

// ══════════════════════════════════════════════════════════════
// 4. TOPBAR ACTIONS
// ══════════════════════════════════════════════════════════════

function bindTopbarActions() {
  on('btn-reset', 'click', () => { if (_ctx.getCurrentViewportId()) { resetViewport(_ctx.getCurrentViewportId()); showToast('🔄 Сброс', 'info', 800); } });
  on('btn-reset-all', 'click', () => { resetAllViewports(); showToast('🔄 Начальное состояние: все окна, включая 3D', 'info', 1500); });
  // [11.1.26] Ctrl+O — сброс ВСЕХ окон (голая O по-прежнему сбрасывает
  // активное): врач просил кнопку «сброс во всех окнах», а хоткей рядом с
  // привычным — то же действие без мыши.
  document.addEventListener('keydown', (e) => {
    if (e.code === 'KeyO' && (e.ctrlKey || e.metaKey) && !e.altKey) {
      const ц = e.target;
      if (ц && (ц.tagName === 'INPUT' || ц.tagName === 'TEXTAREA' || ц.isContentEditable)) return;
      e.preventDefault();
      document.getElementById('btn-reset-all')?.click();
    }
  }, { signal: _getSignal() });   // отписка общим контроллером модуля
  // [6.5.7] Кнопка называется нейтрально; фактическое состояние показано
  // подписью В КАЖДОМ ОКНЕ («выпрямлено по телу» / «как снято»), поэтому
  // путаницы «показывает текущее или следующее» больше нет.
  const _syncStraightenBtn = () => {
    const isBody = getMprAlignMode() === 'body';
    const b = document.getElementById('btn-mpr-straighten');
    if (b) { b.classList.toggle('active', isBody); b.textContent = '⊾ Ориентация MPR'; }
    // [6.7.3] Дубль над центральным ползунком листания держим в том же состоянии.
    const bs = document.getElementById('btn-mpr-straighten-slider');
    if (bs) bs.classList.toggle('active', isBody);
  };
  // [6.7.3] Обе кнопки («Ориентация MPR» в тулбаре и дубль над ползунком) — один обработчик.
  const _doStraightenToggle = () => {
    const { mode, tilt } = toggleMprAlignMode();
    _syncStraightenBtn();
    if (!(tilt > 2)) {
      showToast('Серия снята без наклона — ориентация одна', 'info', 2000);
      return;
    }
    showToast(mode === 'body' ? `⊾ Выпрямлено по телу (наклон ${tilt.toFixed(0)}°)` : '⊿ Показано как снято', 'info', 1800);
  };
  on('btn-mpr-straighten', 'click', _doStraightenToggle);
  on('btn-mpr-straighten-slider', 'click', _doStraightenToggle);
  // [6.7.3] После импорта режим сбрасывается на «как снято» — освежаем подсветку обеих кнопок.
  try {
    busOn(EVENTS.VOLUME_LOAD_DONE, _syncStraightenBtn);
    busOn(EVENTS.STACK_LOADED, _syncStraightenBtn);
  } catch (e) { /* шина недоступна */ }
  _syncStraightenBtn();
  on('btn-invert', 'click', () => { invertViewport(); showToast('🌓 Инверсия', 'info', 800); });
  on('btn-flip-h', 'click', () => { flipViewport('h'); showToast('↔ Отражение Г', 'info', 800); });
  on('btn-flip-v', 'click', () => { flipViewport('v'); showToast('↕ Отражение В', 'info', 800); });
  on('btn-rotate-cw', 'click', () => { rotateViewport(90); showToast('↻ Поворот +90°', 'info', 800); });
  on('btn-rotate-ccw', 'click', () => { rotateViewport(-90); showToast('↺ Поворот −90°', 'info', 800); });
  // [11.1.16] Привязки btn-screenshot / btn-screenshot-anon СНЯТЫ: у пары было
  // два хозяина (exportModule._bindButtons вешал downloadScreenshot, а этот
  // файл — takeScreenshot), и один щелчок скачивал два файла. Clone-защита
  // exportModule не спасала: он инициализируется в uiManager раньше (339<341)
  // и клонировал кнопку ещё пустой. Хозяин теперь один — exportModule:
  // его путь богаче (вжигает оверлеи, форматы, водяной знак). takeScreenshot
  // ниже оставлена как экспорт — её зовёт viewportKeyboard и внешний API.
  on('btn-export-ann', 'click', () => exportAnnotationsFile());
  on('btn-import-ann', 'click', () => importAnnotationsFile());
  // [11.0.9] Привязка btn-print убрана вместе с кнопкой: она была дублем
  // btn-report-print на вкладке «Отчёты» — тот же window.print(), буква в
  // букву. Хозяин остался один, и стоит он рядом с полями отчёта.
  // btn-export-zip / btn-import-zip: единственный хозяин — studyToolsUI.
  // [10.19.32] Пустые обработчики-заглушки убраны: подписка ни на что —
  // это шум для аудита проводки, указателя-комментария достаточно.
  on('btn-save-annotations', 'click', () => _ctx.forceSaveAnnotations());
  on('btn-dicom-tags', 'click', async () => { await _ctx.renderDICOMTags(); _ctx.openModal('modal-dicom-tags'); });
  // [11.0.6] Очистка спрашивает подтверждение. Раньше не спрашивала: кнопка
  // была безымянной корзиной в верхнем ряду, и цена ошибки не бросалась в
  // глаза. А цена высокая — замер владельца на его же исследовании: 5097
  // файлов открываются папкой 38 секунд, на телефоне те же данные папкой —
  // 202 секунды. Промах мимо соседнего пункта меню стоил бы этих минут.
  // Спрашиваем ровно один раз и коротко: сколько исследований закрываем.
  on('btn-clear', 'click', () => {
    const серий = (() => { try { return (_ctx.getAllSeries?.() || []).length; } catch (e) { return 0; } })();
    if (серий === 0) { showToast('Нечего очищать — исследования не загружены', 'info', 2000); return; }
    if (!confirm(`Закрыть загруженные исследования?\n\nСерий в памяти: ${серий}. Файлы на диске не изменятся — их можно открыть заново.`)) return;
    _ctx.clearAllData();
  });
  on('btn-fullscreen', 'click', () => document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen());
  on('btn-help', 'click', () => _ctx.openModal('modal-help'));
  on('btn-clear-ann', 'click', () => { clearAnnotations(); showToast('Разметка очищена', 'info'); });
}

// ══════════════════════════════════════════════════════════════
// 5. VIEWPORT TRANSFORMS (exported)
// ══════════════════════════════════════════════════════════════

/** [6.0.6] «Нормальное отображение» (как у GE): исходный масштаб и ориентация
 *  ВСЕХ 2D-окон разом — снимаются зум, пан, повороты, отражения и инверсия. */
/**
 * [11.1.26] Теперь сбрасываются ВСЕ окна, включая объёмное. Замечание
 * владельца: «Сброс (O) сбрасывает только активное окно — нужна кнопка
 * сброса ВСЕХ окон к начальному состоянию». Кнопка «Норм. отображение»
 * существовала, но 3D-окно пропускала строкой continue — после кувырков
 * объёма врачу оставалось только перезагружать исследование. Для объёма
 * сбрасываем камеру и ставим переднюю проекцию: «начальное состояние» для
 * 3D — это ракурс, а не окно/уровень.
 */
export function resetAllViewports() {
  const re = getRenderingEngine();
  if (!re) return;
  for (const vpId of (getViewportIds() || [])) {
    const vp = re.getViewport(vpId);
    if (!vp) continue;
    if (String(vp.type || '').toLowerCase().includes('3d')) {
      try {
        vp.resetCamera?.();
        setStandardView?.(vpId, 'anterior');   // передняя проекция — исходный ракурс
        vp.render?.();
      } catch (e) { log.debug('[ResetAll] 3D', vpId, e.message); }
      continue;
    }
    try {
      vp.setCamera?.({ flipHorizontal: false, flipVertical: false }); // отражения
      vp.resetProperties?.();  // инверсия, поворот, W/L-пресет
      vp.resetCamera?.();      // зум и пан
      // Поля стопки как при первичной загрузке (лёгкий отступ от краёв)
      if (typeof vp.setImageIdIndex === 'function') {
        const c = vp.getCamera?.();
        if (Number.isFinite(c?.parallelScale)) vp.setCamera({ parallelScale: c.parallelScale * 1.30 });
      }
      vp.render?.();
    } catch (e) { log.debug('[ResetAll]', vpId, e.message); }
  }
}

export function invertViewport() {
  const re = getRenderingEngine();
  const vp = re?.getViewport(_ctx?.getCurrentViewportId?.());
  if (!vp) return;
  const p = vp.getProperties() || {};
  vp.setProperties({ invert: !p.invert });
  vp.render();
}

/**
 * @param {'h'|'v'} dir — horizontal or vertical
 */
export function flipViewport(dir = 'h') {
  const re = getRenderingEngine();
  const vp = re?.getViewport(_ctx?.getCurrentViewportId?.());
  if (!vp) { log.warn('[Flip] No viewport'); return; }
  try {
    const cam = vp.getCamera() || {};
    if (dir === 'h') vp.setCamera({ flipHorizontal: !(cam.flipHorizontal || false) });
    else vp.setCamera({ flipVertical: !(cam.flipVertical || false) });
    vp.render();
  } catch (e) { log.warn('[Flip] Error:', e.message); }
}

/**
 * @param {number} deg — degrees (90, -90, 180, etc.)
 */
export function rotateViewport(deg = 90) {
  const re = getRenderingEngine();
  const vp = re?.getViewport(_ctx?.getCurrentViewportId?.());
  if (!vp) { log.warn('[Rotate] No viewport'); return; }
  try {
    const isStack = vp.type === 'stack';
    if (isStack && vp.setProperties) {
      const currentRotation = vp.getRotation?.() ?? vp.getProperties?.()?.rotation ?? 0;
      const newRotation = ((currentRotation + deg) % 360 + 360) % 360;
      vp.setProperties({ rotation: newRotation });
      vp.render();
      return;
    }
    // VolumeViewport: rotate camera viewUp around viewPlaneNormal
    const cam = vp.getCamera();
    if (cam.viewUp && cam.viewPlaneNormal) {
      const angle = deg * Math.PI / 180;
      const up = cam.viewUp;
      const vpn = cam.viewPlaneNormal;
      const cos = Math.cos(angle), sin = Math.sin(angle);
      const dot = up[0]*vpn[0] + up[1]*vpn[1] + up[2]*vpn[2];
      const cross = [
        up[1]*vpn[2] - up[2]*vpn[1],
        up[2]*vpn[0] - up[0]*vpn[2],
        up[0]*vpn[1] - up[1]*vpn[0]
      ];
      const newUp = [
        up[0]*cos + cross[0]*sin + vpn[0]*dot*(1 - cos),
        up[1]*cos + cross[1]*sin + vpn[1]*dot*(1 - cos),
        up[2]*cos + cross[2]*sin + vpn[2]*dot*(1 - cos)
      ];
      vp.setCamera({ viewUp: newUp });
      vp.render();
    }
  } catch (e) { log.warn('[Rotate] Error:', e.message); }
}

// ══════════════════════════════════════════════════════════════
// 6. SCREENSHOT / ANNOTATIONS I/O (exported)
// ══════════════════════════════════════════════════════════════

/**
 * Take screenshot of current viewport
 * @param {boolean} anonymous — hide PII overlays
 */
export async function takeScreenshot(anonymous = false) {
  try {
    const el = document.querySelector(`#viewport-${_ctx?.getCurrentViewportId?.()}`) || $('viewport-container');
    if (!el) return;
  
    let hiddenEls = [];
    if (anonymous) {
      el.querySelectorAll('.vp-overlay').forEach(ov => {
        if (ov.style.display !== 'none') {
          hiddenEls.push({ el: ov, prev: ov.style.display });
          ov.style.display = 'none';
        }
      });
    }
  
    await screenshotViewport(el, `mikhmel_${anonymous ? 'anon_' : ''}${Date.now()}.png`);
    showToast(anonymous ? '📷 Анонимный снимок сохранён' : '📷 Saved', 'success');
  
    hiddenEls.forEach(({ el, prev }) => { el.style.display = prev; });
  } catch (e) { log.warn(`[toolBindings] takeScreenshot failed:`, e.message);  }
}

export function exportAnnotationsFile() {
  const json = exportAnnotations();
  downloadBlob(new Blob([json], { type: 'application/json' }), `annotations_${Date.now()}.json`);
  showToast('📤 Экспортировано', 'success');
}

export function importAnnotationsFile() {
  const inp = document.createElement('input');
  inp.type = 'file'; inp.accept = '.json';
  inp.addEventListener('change', async (e) => {
    const f = e.target.files[0]; if (!f) return;
    importAnnotations(await f.text());
    showToast('📥 Импортировано', 'success');
  });
  inp.click();
}

// ══════════════════════════════════════════════════════════════
// 7. UNDO / REDO ENGINE
// ══════════════════════════════════════════════════════════════

function _pushAnnotationUndo(action) {
  _annotationUndoStack.push(action);
  if (_annotationUndoStack.length > UNDO_MAX) _annotationUndoStack.shift();
  _annotationRedoStack.length = 0;
  _updateUndoRedoButtons();
}

export { _updateUndoRedoButtons, bindAnnotationEvents, bindUndoRedoKeys };

function _updateUndoRedoButtons() {
  const undoBtn = $('btn-seg-undo');
  const redoBtn = $('btn-seg-redo');
  if (undoBtn) {
    undoBtn.style.opacity = _annotationUndoStack.length > 0 ? '1' : '0.4';
    undoBtn.title = _annotationUndoStack.length > 0 ? `Отменить (${_annotationUndoStack.length})` : 'Нечего отменять';
  }
  if (redoBtn) {
    redoBtn.style.opacity = _annotationRedoStack.length > 0 ? '1' : '0.4';
    redoBtn.title = _annotationRedoStack.length > 0 ? `Повторить (${_annotationRedoStack.length})` : 'Нечего повторять';
  }
}

function _getAnnotationManager() {
  try {
    const csTools = window.cornerstoneTools;
    if (csTools?.annotation?.state) return csTools.annotation.state;
    if (window.MikhmelViewer?._csTools?.annotation?.state) return window.MikhmelViewer._csTools.annotation.state;
  } catch (e) { log.debug('[ToolBindings]', e.message); }
  return null;
}

export function undoAnnotation() {
  if (_annotationUndoStack.length === 0) { showToast('↩ Нечего отменять', 'info', 600); return; }
  const action = _annotationUndoStack.pop();
  const mgr = _getAnnotationManager();
  try {
    if (action.type === 'add' && action.annotation) {
      mgr?.removeAnnotation?.(action.annotation.annotationUID || action.annotation.id);
      _annotationRedoStack.push(action);
      showToast('↩ Аннотация удалена', 'info', 600);
    } else if (action.type === 'remove' && action.annotation) {
      mgr?.addAnnotation?.(action.annotation);
      _annotationRedoStack.push(action);
      showToast('↩ Аннотация восстановлена', 'info', 600);
    } else if (action.type === 'modify' && action.prevState) {
      mgr?.removeAnnotation?.(action.annotation.annotationUID || action.annotation.id);
      mgr?.addAnnotation?.(action.prevState);
      _annotationRedoStack.push({ type: 'modify', annotation: action.prevState, prevState: action.annotation });
      showToast('↩ Изменение отменено', 'info', 600);
    } else {
      _annotationRedoStack.push(action);
      showToast('↩ Отменить (сервис недоступен)', 'info', 800);
    }
  } catch (err) {
    log.warn('[Undo] Error:', err);
    _annotationRedoStack.push(action);
    showToast('↩ Отменить: ' + (err.message || 'ошибка'), 'error', 1000);
  }
  try { const re = getRenderingEngine(); if (re) re.renderViewports(re.getViewports().map(v => v.id)); } catch (e) { log.debug("[toolBindings]", e.message); }
  _updateUndoRedoButtons();
}

export function redoAnnotation() {
  if (_annotationRedoStack.length === 0) { showToast('↪ Нечего повторять', 'info', 600); return; }
  const action = _annotationRedoStack.pop();
  const mgr = _getAnnotationManager();
  try {
    if (action.type === 'add' && action.annotation) {
      mgr?.addAnnotation?.(action.annotation);
      _annotationUndoStack.push(action);
      showToast('↪ Аннотация возвращена', 'info', 600);
    } else if (action.type === 'remove' && action.annotation) {
      mgr?.removeAnnotation?.(action.annotation.annotationUID || action.annotation.id);
      _annotationUndoStack.push(action);
      showToast('↪ Аннотация удалена', 'info', 600);
    } else if (action.type === 'modify' && action.prevState) {
      mgr?.removeAnnotation?.(action.annotation.annotationUID || action.annotation.id);
      mgr?.addAnnotation?.(action.prevState);
      _annotationUndoStack.push({ type: 'modify', annotation: action.prevState, prevState: action.annotation });
      showToast('↪ Изменение возвращено', 'info', 600);
    }
  } catch (err) {
    log.warn('[Redo] Error:', err);
    showToast('↪ Повторить: ' + (err.message || 'ошибка'), 'error', 1000);
  }
  try { const re = getRenderingEngine(); if (re) re.renderViewports(re.getViewports().map(v => v.id)); } catch (e) { log.debug("[toolBindings]", e.message); }
  _updateUndoRedoButtons();
}

/**
 * Enterprise: Get undo/redo stack state
 */
export function getUndoRedoState() {
  return {
    undoCount: _annotationUndoStack.length,
    redoCount: _annotationRedoStack.length,
    undoMax: UNDO_MAX,
    undoTypes: _annotationUndoStack.map(a => a.type),
    redoTypes: _annotationRedoStack.map(a => a.type),
  };
}

/**
 * Enterprise: Clear undo/redo history
 */
export function clearUndoHistory() {
  _annotationUndoStack.length = 0;
  _annotationRedoStack.length = 0;
  _updateUndoRedoButtons();
  showToast('🗑 Отменить/Повторить история очищена', 'info', 1000);
}

// Annotation event bindings
function bindAnnotationEvents() {
  try {
    const csTools = window.cornerstoneTools || window.MikhmelViewer?._csTools;
    if (!csTools?.Enums?.Events) {
      const onReady = () => { document.removeEventListener('cornerstoneToolsReady', onReady); bindAnnotationEvents(); };
      document.addEventListener('cornerstoneToolsReady', onReady);
      setTimeout(() => { document.removeEventListener('cornerstoneToolsReady', onReady); bindAnnotationEvents(); }, 2000);
      return;
    }
    const Events = csTools.Enums.Events;
    const eventTarget = csTools.eventTarget || document;

    if (Events.ANNOTATION_COMPLETED) {
      eventTarget.addEventListener(Events.ANNOTATION_COMPLETED, (evt) => {
        const ann = evt.detail?.annotation;
        if (ann) _pushAnnotationUndo({ type: 'add', annotation: _deepClone(ann) });
      });
    }
    if (Events.ANNOTATION_REMOVED) {
      eventTarget.addEventListener(Events.ANNOTATION_REMOVED, (evt) => {
        const ann = evt.detail?.annotation;
        if (ann) _pushAnnotationUndo({ type: 'remove', annotation: _deepClone(ann) });
      });
    }
    if (Events.ANNOTATION_MODIFIED) {
      eventTarget.addEventListener(Events.ANNOTATION_MODIFIED, (evt) => {
        const ann = evt.detail?.annotation;
        if (ann && evt.detail?.previousAnnotation) {
          _pushAnnotationUndo({ type: 'modify', annotation: _deepClone(ann), prevState: _deepClone(evt.detail.previousAnnotation) });
        }
      });
    }
    log.debug('[UI] Annotation undo/redo events bound');
  } catch (e) {
    log.warn('[UI] Annotation events not available yet:', e);
    setTimeout(bindAnnotationEvents, 3000);
  }
}

function bindUndoRedoKeys() {
  document.addEventListener('keydown', (e) => {
    if (e.ctrlKey || e.metaKey) {
      if (e.key === 'z' && !e.shiftKey) { e.preventDefault(); undoAnnotation(); }
      else if ((e.key === 'z' && e.shiftKey) || e.key === 'y') { e.preventDefault(); redoAnnotation(); }
    }
  });
}

// ══════════════════════════════════════════════════════════════
// 8. TOOL HISTORY & FAVORITES (Enterprise)
// ══════════════════════════════════════════════════════════════

function _pushToolHistory(toolName) {
  _toolHistory.push({ tool: toolName, time: Date.now() });
  if (_toolHistory.length > TOOL_HISTORY_MAX) _toolHistory.shift();
}

/**
 * Get recent tool usage history
 * @returns {Array<{tool:string, time:number}>}
 */
export function getToolHistory() {
  return [..._toolHistory];
}

/**
 * Get favorite tools (persisted)
 * @returns {string[]}
 */
export function getToolFavorites() {
  return [..._toolFavorites];
}

/**
 * Toggle a tool as favorite
 * @param {string} toolName
 * @returns {boolean} — true if now favorite
 */
export function toggleToolFavorite(toolName) {
  if (_toolFavorites.has(toolName)) {
    _toolFavorites.delete(toolName);
  } else {
    _toolFavorites.add(toolName);
  }
  try { localStorage.setItem(FAV_LS_KEY, JSON.stringify([..._toolFavorites])); } catch { /* non-critical */ }
  return _toolFavorites.has(toolName);
}

// ══════════════════════════════════════════════════════════════
// 9. HOTKEYS
// ══════════════════════════════════════════════════════════════

export function initHotkeyActions() {
  initHotkeys({
    cancel: () => { $$('.modal-overlay.open').forEach(m => m.classList.remove('open')); },
    fullscreen: () => document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen(),
    invert: () => invertViewport(),
    resetWL: () => { const vpId = _ctx?.getCurrentViewportId?.(); if (vpId) resetViewport(vpId); },
    layout1x1: () => changeLayout('1x1'),
    layout1x2: () => changeLayout('1x2'),
    layout2x2: () => changeLayout('2x2'),
    layoutMPR: () => changeLayout('mpr'),
    // playCine is handled directly by bindCineSpaceKey — this is a no-op fallback
    playCine: () => {
      // Space key handler does the work via bindCineSpaceKey
      // This exists only for hotkeys.js compatibility
      if (_directCineTimer) {
        clearInterval(_directCineTimer);
        _directCineTimer = null;
        _directCineVpId = null;
      }
    },
    save: () => showToast('💾 Сохранено', 'info'),
    // [11.1.16] Ctrl+E раньше звал takeScreenshot() напрямую и качал ОДИН
    // файл, тогда как сама кнопка (при двух хозяевах) качала два, причём
    // другой реализацией. Теперь хоткей жмёт кнопку: что бы ни делала кнопка,
    // хоткей делает ровно то же — рассинхрон невозможен по построению.
    export: () => document.getElementById('btn-screenshot')?.click(),
    deleteAnnotation: () => showToast('🗑️ Удалить разметку', 'info', 800),
  });
}
